import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshCardStyles, getRefreshPaperStyles } from '../utils/refreshAnimation';
import { metricsService } from '../services/metricsService';
import { serverService } from '../services/serverService';
import { applicationDefinitionService } from '../services/applicationDefinitionService';
import { agentService } from '../services/agentService';
import { healthCheckService } from '../services/healthCheckService';
import { formatDateToTR, formatTimeAgoTR, parseUTCDateToTR, formatTimeOnlyTR, formatDateOnlyTR } from '../utils/dateUtils';
import { filterDiskUsageByAgentThresholds } from '../utils/diskUtils';
import type { Metric, CurrentMetrics, DiskUsage } from '../types/metrics';
import type { Server } from '../types/server';
import type { ApplicationDefinition } from '../types/applicationDefinition';
import type { Agent } from '../types/agent';
import { AgentCapability, capabilityHelper } from '../types/agent';
import type { CurrentHealthStatus } from '../types/healthCheck';
import { HealthStatus } from '../types/healthCheck';
import { dashboardService } from '../services/dashboardService';
import { alertService } from '../services/alertService';
import type { Alert as AlertType } from '../types/alert';
import { AlertStatus, AlertType as AlertTypeEnum } from '../types/alert';

const ServerMetrics = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const { refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const navigate = useNavigate();
  const [server, setServer] = useState<Server | null>(null);
  const [currentMetrics, setCurrentMetrics] = useState<CurrentMetrics | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<Metric[]>([]);
  const [applications, setApplications] = useState<ApplicationDefinition[]>([]);
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, CurrentHealthStatus>>({});
  const [agent, setAgent] = useState<Agent | null>(null);
  const [serverStatus, setServerStatus] = useState<'healthy' | 'warning' | 'critical' | 'unknown'>('healthy');
  const [serverReason, setServerReason] = useState<string | null>(null);
  const [serverReasonDetails, setServerReasonDetails] = useState<string | null>(null);
  const [serverAgentIsOnline, setServerAgentIsOnline] = useState<boolean>(true);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(true);
  const [metricsHistoryLoading, setMetricsHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('24h');

  const loadPageData = useCallback(async () => {
    if (!serverId) return;

    try {
      setLoading(true);
      setError(null);

      const serverData = await serverService.getById(serverId);
      setServer(serverData);

      const agents = await agentService.getAll(serverId);
      if (agents.length > 0) {
        setAgent(agents[0]);
      }

      const currentMetricsData = await metricsService.getCurrentMetrics(serverId).catch(() => null);
      setCurrentMetrics(currentMetricsData);

      const applicationsData = await applicationDefinitionService.getAll(undefined, serverId);
      setApplications(applicationsData);

      const statusesData: Record<string, CurrentHealthStatus> = {};
      await Promise.all(
        applicationsData.map(async (app) => {
          try {
            const status = await healthCheckService.getCurrentStatus(app.id);
            if (status) {
              statusesData[app.id] = status;
            }
          } catch {
            // skip
          }
        })
      );
      setApplicationStatuses(statusesData);

      try {
        const alertsResult = await alertService.getAll({
          serverId,
          status: undefined,
          page: 1,
          pageSize: 1000,
        });
        const unresolvedAlerts = (alertsResult.items || []).filter(
          a => a.status !== AlertStatus.Resolved
        );
        setAlerts(unresolvedAlerts);
      } catch (err: any) {
        console.error('[ServerMetrics] Alert\'ler yüklenirken hata:', err);
        setAlerts([]);
      }

      try {
        const serverDashboard = await dashboardService.getServerDashboard(serverId);
        setServerStatus(serverDashboard.status as 'healthy' | 'warning' | 'critical' | 'unknown');
        setServerReason(serverDashboard.reason || null);
        setServerReasonDetails(serverDashboard.reasonDetails || null);
        setServerAgentIsOnline(serverDashboard.agentIsOnline ?? true);
      } catch (err: any) {
        console.error('[ServerMetrics] Server dashboard yüklenirken hata:', err);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  const loadMetricsHistory = useCallback(async () => {
    if (!serverId) return;

    setMetricsHistoryLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case '1h':
          startDate.setHours(startDate.getHours() - 1);
          break;
        case '6h':
          startDate.setHours(startDate.getHours() - 6);
          break;
        case '24h':
          startDate.setHours(startDate.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
      }
      const historyData = await metricsService.getHistory(serverId, undefined, startDate, endDate, 200).catch(() => []);
      setMetricsHistory(historyData);
    } catch {
      setMetricsHistory([]);
    } finally {
      setMetricsHistoryLoading(false);
    }
  }, [serverId, timeRange]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData, refreshTrigger]);

  useEffect(() => {
    loadMetricsHistory();
  }, [loadMetricsHistory]);

  useEffect(() => {
    if (!autoRefresh || !serverId) return;

    const interval = setInterval(() => {
      loadPageData();
      loadMetricsHistory();
    }, 60000);

    return () => clearInterval(interval);
  }, [autoRefresh, serverId, loadPageData, loadMetricsHistory]);

  const parseDiskUsage = (diskUsageJson: string | null | undefined): DiskUsage => {
    if (diskUsageJson == null || diskUsageJson === '') return {};
    try {
      const parsed = JSON.parse(diskUsageJson);
      if (parsed == null) return {};

      // Array formatı: [{ "Drive": "C:", "UsagePercent": 75.5 }, ...] veya [{ "Device": "C:", "UsagePercent": 75.5 }, ...]
      if (Array.isArray(parsed)) {
        const result: DiskUsage = {};
        parsed.forEach((item: any) => {
          const driveName = item.Drive || item.Device;
          if (driveName && item.UsagePercent !== undefined) {
            result[driveName] = typeof item.UsagePercent === 'number' 
              ? item.UsagePercent 
              : parseFloat(item.UsagePercent) || 0;
          }
        });
        return result;
      }
      
      // Dictionary formatı: { "C:": 75, "D:": 45 }
      if (typeof parsed === 'object' && parsed !== null) {
        const result: DiskUsage = {};
        Object.entries(parsed).forEach(([key, value]) => {
          result[key] = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
        });
        return result;
      }
      
      return {};
    } catch {
      return {};
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!server) {
    return (
      <Layout>
        <Alert severity="error">Sunucu bulunamadı</Alert>
      </Layout>
    );
  }

  const diskUsageRaw = currentMetrics ? parseDiskUsage(currentMetrics.latestDiskUsage ?? undefined) : {};
  const diskUsage = filterDiskUsageByAgentThresholds(diskUsageRaw, agent?.diskThresholds);

  // Tarih formatlamaları artık dateUtils'ten geliyor (Türkiye saati UTC+3)

  const getApplicationStatusDisplay = (app: ApplicationDefinition) => {
    // A. Erişim Kontrolü (En Üst Öncelik)
    
    // 1. Server Agent Durumu
    const serverAgents = agent ? [agent] : [];
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    
    if (!hasOnlineAgent && serverAgents.length > 0) {
      // Server'ın agent'ı var ama offline - unknown (erişim yok)
      return {
        label: 'Bilinmiyor (Erişim Yok)',
        color: 'default' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    }
    
    // 2. AgentHeartbeat Alert Kontrolü
    const serverAlerts = alerts.filter(a => 
      a.serverId === app.serverId && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    
    if (serverAlerts.length > 0) {
      // AgentHeartbeat alert varsa - unknown (erişim yok)
      return {
        label: 'Bilinmiyor (Erişim Yok)',
        color: 'default' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    }
    
    // B. Erişim Varsa Backend'den Gelen Status'u Kullan
    // Backend zaten Health Check ve Alert'i birlikte değerlendiriyor
    const status = applicationStatuses[app.id];
    if (!status) {
      return {
        label: 'Bilinmiyor',
        color: 'warning' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    }

    switch (status.latestHealthStatus) {
      case HealthStatus.Healthy:
        return {
          label: 'Sağlıklı',
          color: 'success' as const,
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
        };
      case HealthStatus.Unhealthy:
        return {
          label: 'Sağlıksız',
          color: 'error' as const,
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
        };
      case HealthStatus.Unknown:
        return {
          label: 'Bilinmiyor',
          color: 'warning' as const,
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
        };
      default:
        return {
          label: 'Bilinmiyor',
          color: 'warning' as const,
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
        };
    }
  };

  // Grafik verisi hazırlama
  const prepareChartData = () => {
    if (metricsHistory.length === 0) {
      return [];
    }

    return metricsHistory.map((metric) => {
      const ramPercent = metric.ramTotal > 0 ? (metric.ramUsage / metric.ramTotal) * 100 : 0;
      return {
        time: parseUTCDateToTR(metric.metricTime).toISOString(),
        cpu: metric.cpuUsage,
        ram: ramPercent,
      };
    });
  };

  const chartData = prepareChartData();

  // Sunucu durumunu hesapla - Dashboard'daki gibi Online/Offline göster
  const getServerStatusDisplay = () => {
    const hasOnlineAgent = serverAgentIsOnline && agent?.isOnline;
    
    if (hasOnlineAgent) {
      return {
        status: 'online' as const,
        label: 'Online',
        icon: CheckCircleIcon,
        iconColor: 'success' as const,
        color: 'success' as const,
      };
    } else {
      return {
        status: 'offline' as const,
        label: 'Offline',
        icon: ErrorIcon,
        iconColor: 'error' as const,
        color: 'error' as const,
      };
    }
  };

  const serverStatusDisplay = getServerStatusDisplay();
  const StatusIcon = serverStatusDisplay.icon;

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1">
            {server.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tenant: {server.tenantName || 'N/A'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Server Overview */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          Server Overview
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: { xs: '100%', sm: '200px' }, ...getRefreshCardStyles(isRefreshing) }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Durum
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <StatusIcon color={serverStatusDisplay.iconColor} />
                <Typography variant="h6">{serverStatusDisplay.label}</Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: { xs: '100%', sm: '200px' }, ...getRefreshCardStyles(isRefreshing) }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Agent
              </Typography>
              <Box display="flex" flexDirection="column" gap={1} mt={1}>
                {agent ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1}>
                      {agent.isActive ? (
                        <>
                          <CheckCircleIcon color="success" />
                          <Typography variant="h6">Aktif</Typography>
                        </>
                      ) : (
                        <>
                          <ErrorIcon color="error" />
                          <Typography variant="h6">Pasif</Typography>
                        </>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {agent.isOnline ? (
                        <>
                          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">Online</Typography>
                        </>
                      ) : (
                        <>
                          <ErrorIcon color="error" sx={{ fontSize: 18 }} />
                          <Typography variant="body2" color="error">Erişim Yok</Typography>
                        </>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Versiyon: {agent.agentVersion}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Agent bulunamadı
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: { xs: '100%', sm: '200px' }, ...getRefreshCardStyles(isRefreshing) }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Son Güncelleme
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {agent?.lastHeartbeat ? formatTimeAgoTR(agent.lastHeartbeat) : 'Bilinmiyor'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3, ...getRefreshPaperStyles(isRefreshing) }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Metrics" />
          <Tab label="Uygulamalar" />
        </Tabs>
      </Paper>

      {/* Metrics Tab */}
      {activeTab === 0 && (
        <>
          {currentMetrics && (
            <Box display="flex" flexDirection="column" gap={2} mb={3}>
              <Box display="flex" gap={2} flexWrap="wrap">
                {(() => {
                  // Agent yoksa tüm metrikleri göster; agent varsa sadece açık özellikleri göster
                  const hasCpuCap = !agent || capabilityHelper.hasCapability(agent.capabilities, AgentCapability.CpuMonitoring);
                  const hasRamCap = !agent || capabilityHelper.hasCapability(agent.capabilities, AgentCapability.MemoryMonitoring);
                  const cpuWarningThreshold = agent?.cpuWarningThreshold ?? 80;
                  const cpuCriticalThreshold = agent?.cpuCriticalThreshold ?? 90;
                  const ramWarningThreshold = agent?.ramWarningThreshold ?? 80;
                  const ramCriticalThreshold = agent?.ramCriticalThreshold ?? 90;
                  const cpuExceedsCritical = currentMetrics.latestCpuUsage >= cpuCriticalThreshold;
                  const cpuExceedsWarning = currentMetrics.latestCpuUsage >= cpuWarningThreshold && !cpuExceedsCritical;
                  const ramExceedsCritical = currentMetrics.latestRamUsagePercent >= ramCriticalThreshold;
                  const ramExceedsWarning = currentMetrics.latestRamUsagePercent >= ramWarningThreshold && !ramExceedsCritical;
                  return (
                    <>
                      {hasCpuCap && (
                      <Card sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' }, minWidth: { xs: '100%', md: '250px' }, position: 'relative', ...getRefreshCardStyles(isRefreshing) }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              CPU Kullanımı
                            </Typography>
                            {(cpuExceedsCritical || cpuExceedsWarning) && (
                              <Tooltip 
                                title={cpuExceedsCritical 
                                  ? `CPU kullanımı kritik seviyede: %${currentMetrics.latestCpuUsage.toFixed(1)} >= %${cpuCriticalThreshold.toFixed(1)}`
                                  : `CPU kullanımı yüksek: %${currentMetrics.latestCpuUsage.toFixed(1)} >= %${cpuWarningThreshold.toFixed(1)}`} 
                                arrow 
                                placement="top"
                              >
                                {cpuExceedsCritical ? (
                                  <ErrorIcon 
                                    sx={{ 
                                      fontSize: '1.25rem',
                                      color: 'error.main',
                                      cursor: 'help',
                                    }} 
                                  />
                                ) : (
                                  <WarningIcon 
                                    sx={{ 
                                      fontSize: '1.25rem',
                                      color: 'warning.main',
                                      cursor: 'help',
                                    }} 
                                  />
                                )}
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="h4" color="primary.main">
                            {currentMetrics.latestCpuUsage.toFixed(1)}%
                          </Typography>
                          {currentMetrics.averageCpuUsage !== undefined && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Ortalama (1 saat): {currentMetrics.averageCpuUsage.toFixed(1)}%
                            </Typography>
                          )}
                          <LinearProgress
                            variant="determinate"
                            value={currentMetrics.latestCpuUsage}
                            color={currentMetrics.latestCpuUsage >= cpuCriticalThreshold ? 'error' : currentMetrics.latestCpuUsage >= cpuWarningThreshold ? 'warning' : 'primary'}
                            sx={{ mt: 2 }}
                          />
                        </CardContent>
                      </Card>
                      )}
                      {hasRamCap && (
                      <Card sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' }, minWidth: { xs: '100%', md: '250px' }, position: 'relative', ...getRefreshCardStyles(isRefreshing) }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              RAM Kullanımı
                            </Typography>
                            {(ramExceedsCritical || ramExceedsWarning) && (
                              <Tooltip 
                                title={ramExceedsCritical 
                                  ? `RAM kullanımı kritik seviyede: %${currentMetrics.latestRamUsagePercent.toFixed(1)} >= %${ramCriticalThreshold.toFixed(1)}`
                                  : `RAM kullanımı yüksek: %${currentMetrics.latestRamUsagePercent.toFixed(1)} >= %${ramWarningThreshold.toFixed(1)}`} 
                                arrow 
                                placement="top"
                              >
                                {ramExceedsCritical ? (
                                  <ErrorIcon 
                                    sx={{ 
                                      fontSize: '1.25rem',
                                      color: 'error.main',
                                      cursor: 'help',
                                    }} 
                                  />
                                ) : (
                                  <WarningIcon 
                                    sx={{ 
                                      fontSize: '1.25rem',
                                      color: 'warning.main',
                                      cursor: 'help',
                                    }} 
                                  />
                                )}
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="h4" color="primary.main">
                            {currentMetrics.latestRamUsagePercent.toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {currentMetrics.latestRamUsage.toFixed(2)} GB / {currentMetrics.latestRamTotal.toFixed(2)} GB
                          </Typography>
                          {currentMetrics.averageRamUsagePercent !== undefined && (
                            <Typography variant="body2" color="text.secondary">
                              Ortalama (1 saat): {currentMetrics.averageRamUsagePercent.toFixed(1)}%
                            </Typography>
                          )}
                          <LinearProgress
                            variant="determinate"
                            value={currentMetrics.latestRamUsagePercent}
                            color={currentMetrics.latestRamUsagePercent >= ramCriticalThreshold ? 'error' : currentMetrics.latestRamUsagePercent >= ramWarningThreshold ? 'warning' : 'primary'}
                            sx={{ mt: 2 }}
                          />
                        </CardContent>
                      </Card>
                      )}
                    </>
                  );
                })()}

                <Card sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' }, minWidth: { xs: '100%', md: '250px' }, ...getRefreshCardStyles(isRefreshing) }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Son Güncelleme
                    </Typography>
                    <Typography variant="h6">
                      {formatDateToTR(currentMetrics.latestMetricTime)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {(!agent || capabilityHelper.hasCapability(agent.capabilities, AgentCapability.DiskMonitoring)) && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Disk Kullanımı
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {Object.keys(diskUsage).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Disk verisi henüz gelmedi. Agent bir sonraki metrik gönderiminde güncelleyecek.
                        </Typography>
                      ) : (() => {
                        // Disk threshold'larını parse et
                        let diskThresholds: Array<{ drive: string; warning: number; critical: number }> | null = null;
                        if (agent?.diskThresholds) {
                          try {
                            diskThresholds = JSON.parse(agent.diskThresholds);
                          } catch {
                            diskThresholds = null;
                          }
                        }
                        
                        return Object.entries(diskUsage).map(([drive, usage]) => {
                          const usageNum = typeof usage === 'number' ? usage : parseFloat(String(usage)) || 0;
                          
                          // Bu disk için threshold'ları bul
                          const normalizedDrive = drive.trim().toUpperCase();
                          const driveWithColon = normalizedDrive.endsWith(':') ? normalizedDrive : normalizedDrive + ':';
                          
                          let diskWarningThreshold = 80;
                          let diskCriticalThreshold = 90;
                          let isThisDriveAlerted = false;
                          let alertMessage = '';
                          
                          if (diskThresholds && diskThresholds.length > 0) {
                            const diskConfig = diskThresholds.find((d: any) => {
                              const normalized = (d.drive?.trim().toUpperCase() || '').endsWith(':') 
                                ? d.drive?.trim().toUpperCase() 
                                : d.drive?.trim().toUpperCase() + ':';
                              return normalized === driveWithColon;
                            });
                            
                            if (diskConfig) {
                              diskWarningThreshold = diskConfig.warning || 80;
                              diskCriticalThreshold = diskConfig.critical || 90;
                              
                              // Threshold kontrolü
                              if (usageNum >= diskCriticalThreshold) {
                                isThisDriveAlerted = true;
                                alertMessage = `${drive} sürücüsü kritik seviyede dolu: %${usageNum.toFixed(1)} >= %${diskCriticalThreshold.toFixed(1)}`;
                              } else if (usageNum >= diskWarningThreshold) {
                                isThisDriveAlerted = true;
                                alertMessage = `${drive} sürücüsü yüksek seviyede dolu: %${usageNum.toFixed(1)} >= %${diskWarningThreshold.toFixed(1)}`;
                              }
                            }
                            // Eğer disk threshold'ları varsa ama bu disk için threshold yoksa, alert gösterme
                          } else {
                            // Disk threshold yoksa default threshold'ları kullan
                            if (usageNum >= diskCriticalThreshold) {
                              isThisDriveAlerted = true;
                              alertMessage = `${drive} sürücüsü kritik seviyede dolu: %${usageNum.toFixed(1)} >= %${diskCriticalThreshold.toFixed(1)}`;
                            } else if (usageNum >= diskWarningThreshold) {
                              isThisDriveAlerted = true;
                              alertMessage = `${drive} sürücüsü yüksek seviyede dolu: %${usageNum.toFixed(1)} >= %${diskWarningThreshold.toFixed(1)}`;
                            }
                          }
                          
                          const isCritical = usageNum >= diskCriticalThreshold;
                          
                          return (
                            <Box key={drive} display="flex" gap={1}>
                              {/* İlk Sütun: Disk adı, değer ve bar */}
                              <Box flex={1}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                  <Typography variant="body2" color="text.secondary">
                                    {drive}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="bold"
                                    sx={{
                                      color: 
                                        usageNum >= diskCriticalThreshold
                                          ? 'error.main'
                                          : usageNum >= diskWarningThreshold
                                          ? 'warning.main'
                                          : 'text.primary',
                                    }}
                                  >
                                    {usageNum.toFixed(1)}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={usageNum}
                                  color={usageNum >= diskCriticalThreshold ? 'error' : usageNum >= diskWarningThreshold ? 'warning' : 'primary'}
                                />
                              </Box>
                              {/* İkinci Sütun: Uyarı ikonu (varsa) */}
                              {isThisDriveAlerted && (
                                <Box 
                                  display="flex" 
                                  alignItems="center" 
                                  justifyContent="center"
                                  sx={{ minWidth: 24 }}
                                >
                                  <Tooltip title={alertMessage} arrow placement="top">
                                    {isCritical ? (
                                      <ErrorIcon 
                                        sx={{ 
                                          fontSize: '1rem',
                                          color: 'error.main',
                                          cursor: 'help',
                                        }} 
                                      />
                                    ) : (
                                      <WarningIcon 
                                        sx={{ 
                                          fontSize: '1rem',
                                          color: 'warning.main',
                                          cursor: 'help',
                                        }} 
                                      />
                                    )}
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

          {/* Metrics Grafikleri - sadece bu bölüm zaman aralığına göre yüklenir */}
          <Box display="flex" flexDirection="column" gap={3} mb={3}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Box display="flex" gap={1}>
                {(['1h', '6h', '24h', '7d'] as const).map((range) => (
                  <Button
                    key={range}
                    variant={timeRange === range ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setTimeRange(range)}
                    disabled={metricsHistoryLoading}
                  >
                    {range === '1h' ? '1 Saat' : range === '6h' ? '6 Saat' : range === '24h' ? '24 Saat' : '7 Gün'}
                  </Button>
                ))}
              </Box>
              {metricsHistoryLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Grafik verisi yükleniyor...</Typography>
                </Box>
              )}
            </Box>

            {metricsHistoryLoading ? (
              <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50], borderRadius: 3, py: 6 }}>
                <CircularProgress />
              </Box>
            ) : chartData.length > 0 ? (
          <>
            {/* CPU Grafiği */}
            <Paper sx={{ p: 3, borderRadius: 3, ...getRefreshPaperStyles(isRefreshing) }}>
              <Typography variant="h6" gutterBottom>
                CPU Kullanımı
              </Typography>
              <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(value) => {
                        const date = parseUTCDateToTR(value);
                        return timeRange === '1h' || timeRange === '6h'
                          ? formatTimeOnlyTR(value)
                          : timeRange === '24h'
                          ? `${formatDateOnlyTR(value)} ${date.getHours()}:00`
                          : formatDateOnlyTR(value);
                      }}
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis domain={[0, 100]} stroke="#64748b" style={{ fontSize: '12px' }} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                      }}
                      formatter={(value: number | undefined) => [`${value?.toFixed(1)}%`, 'CPU']}
                      labelFormatter={(value) => formatDateToTR(value)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="cpu" name="CPU" stroke="#1976d2" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* RAM Grafiği */}
            <Paper sx={{ p: 3, borderRadius: 3, ...getRefreshPaperStyles(isRefreshing) }}>
              <Typography variant="h6" gutterBottom>
                RAM Kullanımı
              </Typography>
              <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(value) => {
                        const date = parseUTCDateToTR(value);
                        return timeRange === '1h' || timeRange === '6h'
                          ? `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`
                          : timeRange === '24h'
                          ? `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`
                          : `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis domain={[0, 100]} stroke="#64748b" style={{ fontSize: '12px' }} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                      }}
                      formatter={(value: number | undefined) => [`${value?.toFixed(1)}%`, 'RAM']}
                      labelFormatter={(value) => formatDateToTR(String(value))}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="ram" name="RAM" stroke="#1976d2" fill="url(#colorRam)" strokeWidth={2} connectNulls />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Bu zaman aralığında metrik verisi yok.</Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Applications Tab */}
      {activeTab === 1 && (
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Uygulamalar ({applications.length})
          </Typography>
          {applications.length === 0 ? (
            <Alert severity="info">Bu sunucuda henüz uygulama tanımı bulunmuyor.</Alert>
          ) : (
            <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Uygulama</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Tip</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Durum</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Son Kontrol</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((app, index) => {
                    const statusDisplay = getApplicationStatusDisplay(app);
                    const status = applicationStatuses[app.id];
                    return (
                      <TableRow
                        key={app.id}
                        hover
                        sx={{
                          bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                          '&:hover': { bgcolor: 'action.selected' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {app.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                          <Chip label={app.applicationTypeName} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                          <Chip
                            icon={statusDisplay.icon}
                            label={statusDisplay.label}
                            color={statusDisplay.color}
                            size="small"
                            sx={{ fontWeight: 600, height: 22, fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            {status?.latestCheckTime ? formatTimeAgoTR(status.latestCheckTime) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/application-definitions/${app.id}/history`)}
                          >
                            Detay
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      </Box>
    </Layout>
  );
};

export default ServerMetrics;







